import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useUserFavoritesContext } from '../contexts/UserFavoritesContext';
import { UserFavorite } from '../types/userFavorites';
import { getKindDisplayName } from '../utils/urlUtils';
import { useTrackedLinkModal } from '../hooks';
import { ContentModal } from '../components/ui';
import { ResourceHistory, ResourceSession } from '../types/userActivity';
import { formatDuration, getEffectiveSortDate } from '../utils/favoritesUtils';
import { formatRelativeTime } from '../utils/time';
import {
  Heart,
  Search,
  Filter,
  Clock,
  Video,
  Activity,
  MessageSquare,
  FileText,
  AlertCircle,
  Headphones,
  ChefHat,
  Users,
  Edit3,
  RefreshCw,
  X,
  ArrowUpDown,
  LayoutList,
  History,
  Trash2
} from 'lucide-react';

type KindFilter = 'all' | 'video' | 'audio' | 'recipe' | 'article' | 'meeting' | 'manual' | 'chat' | 'activity';
type ActiveTab = 'resources';
type SortOption = 'lastAccessed' | 'mostVisited' | 'mostTime' | 'recentlyAdded';

const SORT_LABELS: Record<SortOption, string> = {
  lastAccessed: 'Last Accessed',
  mostVisited: 'Most Visited',
  mostTime: 'Most Time Spent',
  recentlyAdded: 'Recently Added',
};

const FavoritesPage: React.FC = () => {
  const {
    favorites,
    loading,
    loadingMore,
    error,
    hasMore,
    toggleFavoriteFlag,
    deleteFavorite,
    loadMore,
    refresh,
    insights
  } = useUserFavoritesContext();

  const { isOpen, url, title, showOpenInNewTab, allowCredentials, trustDomain, openLink, closeModal } = useTrackedLinkModal();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [activeTab, setActiveTab] = useState<ActiveTab>('resources');
  const [sortBy, setSortBy] = useState<SortOption>('lastAccessed');
  const [togglingFlagId, setTogglingFlagId] = useState<string | null>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [historyItemId, setHistoryItemId] = useState<string | null>(null);
  const [onlyManual, setOnlyManual] = useState(false);

  // Get unique kinds from favorites for filter tabs
  const availableKinds = useMemo(() => {
    const kinds = new Set(favorites.map(f => f.kind));
    return Array.from(kinds);
  }, [favorites]);

  const hasManualItems = useMemo(() => favorites.some(f => f.payload?.metadata?.isFavorited === true), [favorites]);
  const effectiveOnlyManual = onlyManual && hasManualItems;

  // Filter favorites based on search, kind, and favorites-only toggle
  const filteredFavorites = useMemo(() => {
    return favorites.filter(item => {
      const matchesKind = kindFilter === 'all' || item.kind === kindFilter;
      const matchesSearch = !searchQuery || 
        item.payload.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.payload.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesManual = !effectiveOnlyManual || item.payload?.metadata?.isFavorited === true;
      return matchesKind && matchesSearch && matchesManual;
    });
  }, [favorites, kindFilter, searchQuery, effectiveOnlyManual]);

  // Sorted resources for the Resources tab (flat list)
  const sortedResources = useMemo(() => {
    const items = [...filteredFavorites];
    switch (sortBy) {
      case 'lastAccessed':
        return items.sort((a, b) => getEffectiveSortDate(b) - getEffectiveSortDate(a));
      case 'mostVisited': {
        const getCount = (f: UserFavorite) => (f.payload?.metadata?.history as ResourceHistory | undefined)?.sessionCount || 0;
        return items.sort((a, b) => getCount(b) - getCount(a));
      }
      case 'mostTime': {
        const getTime = (f: UserFavorite) => (f.payload?.metadata?.history as ResourceHistory | undefined)?.totalTimeMs || 0;
        return items.sort((a, b) => getTime(b) - getTime(a));
      }
      case 'recentlyAdded':
        return items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      default:
        return items;
    }
  }, [filteredFavorites, sortBy]);

  // Icon helper
  const getKindIcon = useCallback((kind: string) => {
    switch (kind) {
      case 'chat': return MessageSquare;
      case 'video': return Video;
      case 'audio': return Headphones;
      case 'recipe': return ChefHat;
      case 'article': return FileText;
      case 'meeting': return Users;
      case 'manual': return Edit3;
      case 'activity': return Activity;
      default: return Clock;
    }
  }, []);

  // Toggle isFavorited flag handler
  const handleToggleFlag = useCallback(async (itemId: string) => {
    setTogglingFlagId(itemId);
    try {
      await toggleFavoriteFlag(itemId);
    } catch (err) {
      console.error('Failed to toggle favorite flag:', err);
    } finally {
      setTogglingFlagId(null);
    }
  }, [toggleFavoriteFlag]);

  // Delete handler for non-favorited items
  const handleDelete = useCallback(async (itemId: string) => {
    setDeletingItemId(itemId);
    try {
      await deleteFavorite(itemId);
    } catch (err) {
      console.error('Failed to delete item:', err);
    } finally {
      setDeletingItemId(null);
      setConfirmingDeleteId(null);
    }
  }, [deleteFavorite]);

  const filterTabs: { key: KindFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    ...availableKinds.map(kind => ({
      key: kind as KindFilter,
      label: getKindDisplayName(kind as any) || kind
    }))
  ];

  // Format time range for timeline entries
  const formatTimeRange = useCallback((session: ResourceSession) => {
    const start = new Date(session.openedAt);
    const end = new Date(session.closedAt);
    const fmt = (d: Date) => d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return `${fmt(start)} – ${fmt(end)}`;
  }, []);

  return (
    <div className="h-full flex flex-col bg-rm-bg-0">
      {/* Header */}
      <div className="bg-rm-bg-0 border-b border-rm-neutral-600/20 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-rm-accent-300/20 rounded-full flex items-center justify-center">
              <Heart className="w-5 h-5 text-rm-accent-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-rm-primary-700">My Resources</h1>
              <p className="text-sm text-rm-neutral-600">
                {favorites.length} saved item{favorites.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={() => refresh()}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm text-rm-neutral-600 hover:text-rm-primary-700 hover:bg-rm-bg-50 rounded-card transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>

        {/* Tabs + Compact Stats */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setActiveTab('resources')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-card transition-colors ${
                activeTab === 'resources'
                  ? 'bg-rm-primary-700 text-white'
                  : 'text-rm-neutral-600 hover:bg-rm-bg-50'
              }`}
            >
              <LayoutList className="w-4 h-4" />
              Resources
            </button>
          </div>

          {/* Compact Stats — temporarily hidden */}
          {false && (insights.totalFavorites > 0 || insights.totalResourcesViewed > 0) && (
            <div className="hidden sm:flex items-center gap-4">
              <div className="text-center">
                <div className="text-sm font-semibold text-rm-ink-900">{insights.totalFavorites}</div>
                <div className="text-[10px] text-rm-neutral-600">Favorites</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold text-rm-ink-900">{insights.totalResourcesViewed}</div>
                <div className="text-[10px] text-rm-neutral-600">Viewed</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold text-rm-ink-900">{formatDuration(insights.totalTimeSpentMs)}</div>
                <div className="text-[10px] text-rm-neutral-600">Time Spent</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold text-rm-ink-900">{insights.totalSessions}</div>
                <div className="text-[10px] text-rm-neutral-600">Sessions</div>
              </div>
            </div>
          )}
        </div>

        {/* Search, Filter, and Sort */}
        {/* Expanded search takes full width */}
        {isSearchOpen && (
          <div className="relative animate-[fadeIn_150ms_ease-out]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rm-neutral-600" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search favorites..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onBlur={() => { if (!searchQuery) setIsSearchOpen(false); }}
              className="w-full max-w-md pl-10 pr-9 py-2 border border-rm-neutral-600/30 rounded-card text-sm focus:ring-2 focus:ring-rm-accent-500 focus:border-rm-accent-500"
            />
            <button
              onClick={() => { setSearchQuery(''); setIsSearchOpen(false); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-rm-neutral-600 hover:text-rm-ink-900"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {/* Search icon (collapsed) */}
          {!isSearchOpen && (
            <button
              onClick={() => {
                setIsSearchOpen(true);
                setTimeout(() => searchInputRef.current?.focus(), 0);
              }}
              className="p-2 rounded-card border border-rm-neutral-600/30 text-rm-neutral-600 hover:text-rm-ink-900 hover:bg-rm-bg-50 transition-colors"
              title="Search favorites"
            >
              <Search className="w-4 h-4" />
            </button>
          )}
            {/* Sort Dropdown (Resources tab only) */}
            {activeTab === 'resources' && (
              <div className="relative flex items-center gap-1">
                <ArrowUpDown className="w-4 h-4 text-rm-neutral-600" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="appearance-none bg-rm-bg-50 border border-rm-neutral-600/30 rounded-card px-3 py-1.5 text-xs font-medium text-rm-neutral-600 focus:ring-2 focus:ring-rm-accent-500 cursor-pointer pr-6"
                >
                  {(Object.keys(SORT_LABELS) as SortOption[]).map(key => (
                    <option key={key} value={key}>{SORT_LABELS[key]}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Only Favorites Toggle */}
            <button
              onClick={() => setOnlyManual(v => !v)}
              disabled={!hasManualItems}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                effectiveOnlyManual
                  ? 'bg-rm-accent-500 text-white'
                  : 'bg-rm-bg-50 text-rm-neutral-600 hover:bg-rm-neutral-600/10'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              Only Favorites
            </button>

            {/* Kind Filter Tabs */}
            {filterTabs.length > 1 && (
              <div className="flex items-center gap-1 flex-wrap">
                <Filter className="w-4 h-4 text-rm-neutral-600 mr-1" />
                {filterTabs.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setKindFilter(tab.key)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                      kindFilter === tab.key
                        ? 'bg-rm-primary-700 text-white'
                        : 'bg-rm-bg-50 text-rm-neutral-600 hover:bg-rm-neutral-600/10'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {/* Loading State */}
        {loading && favorites.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-rm-accent-300 border-t-rm-accent-600 rounded-full animate-spin mb-4" />
            <p className="text-sm text-rm-neutral-600">Loading favorites...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-card">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button onClick={() => refresh()} className="mt-2 text-sm text-red-600 hover:underline">
              Try again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredFavorites.length === 0 && activeTab === 'resources' && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-20 h-20 bg-rm-bg-50 rounded-full flex items-center justify-center mb-4">
              <Heart className="w-10 h-10 text-rm-neutral-600" />
            </div>
            <h3 className="text-lg font-medium text-rm-ink-900 mb-2">
              {searchQuery || kindFilter !== 'all' ? 'No matching favorites' : 'No favorites yet'}
            </h3>
            <p className="text-sm text-rm-neutral-600 text-center max-w-sm">
              {searchQuery || kindFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'Start adding favorites by clicking the + icon next to resources in chat conversations.'}
            </p>
            {(searchQuery || kindFilter !== 'all') && (
              <button
                onClick={() => { setSearchQuery(''); setKindFilter('all'); }}
                className="mt-4 text-sm text-rm-accent-600 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* ===== RESOURCES TAB ===== */}
        {activeTab === 'resources' && !loading && !error && sortedResources.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedResources.map(item => {
              const Icon = getKindIcon(item.kind);
              const linkUrl = item.payload?.url || item.payload?.metadata?.link;
              const isFavorited = item.payload?.metadata?.isFavorited === true;

              return (
                <div
                  key={item.id}
                  className="bg-white border border-rm-neutral-600/20 rounded-card p-4 shadow-soft hover:shadow-card transition-shadow"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-rm-bg-50 text-rm-neutral-600"
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      {linkUrl ? (
                        <button
                          onClick={() => openLink(linkUrl, item.payload.title)}
                          className="text-left group"
                        >
                          <h3 className="text-sm font-medium text-rm-primary-700 group-hover:text-rm-accent-600 transition-colors line-clamp-2">
                            {item.payload.title}
                          </h3>
                        </button>
                      ) : (
                        <h3 className="text-sm font-medium text-rm-ink-900 line-clamp-2">
                          {item.payload.title}
                        </h3>
                      )}
                      
                      {item.payload.description && item.payload.description !== item.payload.title && (
                        <p className="text-xs text-rm-neutral-600 mt-1 line-clamp-2">
                          {item.payload.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Bottom row: stats left, actions right */}
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-rm-neutral-600/10">
                    {/* Stats */}
                    {(() => {
                      const history = item.payload?.metadata?.history as ResourceHistory | undefined;
                      return (
                        <span className="text-xs text-rm-neutral-600">
                          {history && history.sessionCount > 0
                            ? formatRelativeTime(history.lastAccessedAt)
                            : `Added ${formatRelativeTime(item.createdAt)}`}
                        </span>
                      );
                    })()}

                    {/* Action buttons */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleToggleFlag(item.id)}
                        disabled={togglingFlagId === item.id}
                        className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                          isFavorited
                            ? 'text-red-500 hover:text-red-400'
                            : 'text-rm-neutral-400 hover:text-red-500'
                        } ${togglingFlagId === item.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        {togglingFlagId === item.id ? (
                          <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Heart className={`w-3.5 h-3.5 ${isFavorited ? 'fill-current' : ''}`} />
                        )}
                      </button>
                      {/* History button */}
                      {(() => {
                        const hist = item.payload?.metadata?.history as ResourceHistory | undefined;
                        if (!hist || hist.sessionCount === 0) return null;
                        return (
                          <button
                            onClick={() => setHistoryItemId(historyItemId === item.id ? null : item.id)}
                            className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                              historyItemId === item.id
                                ? 'text-rm-primary-700 bg-rm-bg-50'
                                : 'text-rm-neutral-400 hover:text-rm-primary-700'
                            }`}
                            title="View history"
                          >
                            <History className="w-3.5 h-3.5" />
                          </button>
                        );
                      })()}
                      {!isFavorited && (
                        <button
                          onClick={() => setConfirmingDeleteId(item.id)}
                          disabled={deletingItemId === item.id}
                          className="w-7 h-7 rounded-full flex items-center justify-center text-rm-neutral-400 hover:text-red-600 transition-colors"
                          title="Delete permanently"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Inline delete confirmation */}
                  {confirmingDeleteId === item.id && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-red-700">
                          This item will be permanently deleted, including all timeline history. This action cannot be undone.
                        </p>
                      </div>
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => setConfirmingDeleteId(null)}
                          disabled={deletingItemId === item.id}
                          className="px-3 py-1 text-xs font-medium text-rm-neutral-600 bg-white border border-rm-neutral-600/30 rounded-card hover:bg-rm-bg-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          disabled={deletingItemId === item.id}
                          className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded-card hover:bg-red-700 transition-colors disabled:opacity-50"
                        >
                          {deletingItemId === item.id ? (
                            <span className="flex items-center gap-1">
                              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Deleting…
                            </span>
                          ) : (
                            'Delete'
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Inline History Panel */}
                  {historyItemId === item.id && (() => {
                    const hist = item.payload?.metadata?.history as ResourceHistory | undefined;
                    if (!hist?.sessions?.length) return null;
                    const sessions = [...hist.sessions].sort((a, b) => new Date(b.closedAt).getTime() - new Date(a.closedAt).getTime());
                    return (
                      <div className="mt-3 p-3 bg-rm-bg-50 border border-rm-neutral-600/10 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <History className="w-3.5 h-3.5 text-rm-primary-700" />
                            <span className="text-xs font-semibold text-rm-ink-900">Viewing History</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-rm-neutral-600">
                            <span>{hist.sessionCount} session{hist.sessionCount !== 1 ? 's' : ''}</span>
                            <span className="inline-flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDuration(hist.totalTimeMs)}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto">
                          {sessions.map((session, idx) => {
                            const date = new Date(session.closedAt);
                            const dayStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                            const timeStr = formatTimeRange(session);
                            return (
                              <div key={idx} className="flex items-center justify-between text-xs py-1 px-2 rounded bg-white">
                                <span className="text-rm-neutral-600">{dayStr}</span>
                                <span className="text-rm-neutral-600">{timeStr}</span>
                                <span className="inline-flex items-center gap-1 text-rm-ink-900 font-medium">
                                  <Clock className="w-3 h-3" />
                                  {formatDuration(session.durationMs)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}

        {/* Load More (Resources tab) */}
        {activeTab === 'resources' && hasMore && filteredFavorites.length > 0 && (
          <div className="flex justify-center mt-8">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="px-6 py-2 text-sm font-medium text-rm-primary-700 bg-white border border-rm-neutral-600/20 rounded-card hover:bg-rm-bg-50 transition-colors disabled:opacity-50"
            >
              {loadingMore ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-rm-accent-300 border-t-rm-accent-600 rounded-full animate-spin" />
                  Loading...
                </span>
              ) : (
                'Load more'
              )}
            </button>
          </div>
        )}
      </div>

      {/* Content Modal */}
      <ContentModal
        url={url}
        title={title}
        isOpen={isOpen}
        onClose={closeModal}
        showOpenInNewTab={showOpenInNewTab}
        allowCredentials={allowCredentials}
        trustDomain={trustDomain}
      />
    </div>
  );
};

export default FavoritesPage;
